<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RacePricing } from '@/types/events'
import { useDismissablePopover } from '@/composables/useDismissablePopover'
import { formatCentsAsCurrency } from '@/utils/currencyFormat'
import { formatPriceChangeDate } from '@/utils/racePriceTier'

const props = defineProps<{
  pricing: RacePricing
}>()

const { t, locale } = useI18n()

const priceText = computed<string>(() =>
  formatCentsAsCurrency(props.pricing.current_price_cents, props.pricing.currency, locale.value),
)

const tierName = computed<string | null>(() => props.pricing.current_tier?.name ?? null)

// Server value as is: already the lower of the tier's and the race's remaining spots.
const spotsText = computed<string | null>(() => {
  const spots = props.pricing.current_tier?.remaining_spots
  if (spots === null || spots === undefined) return null
  return t('public.race_price.spots_left', { count: spots }, spots)
})

// Crossed-out standard price, only when the current (tier) price is lower.
const standardPriceText = computed<string | null>(() =>
  props.pricing.current_price_cents < props.pricing.standard_price_cents
    ? formatCentsAsCurrency(props.pricing.standard_price_cents, props.pricing.currency, locale.value)
    : null,
)

const pillRef = ref<HTMLButtonElement | null>(null)
const popoverRef = ref<HTMLElement | null>(null)

// Only one tier popover is open at a time, across all single-open popovers on the page.
// Escape is handled in the capture phase so it is stopped before an enclosing slide-over or dialog sees it.
const popover = useDismissablePopover({
  idPrefix: 'race-price-tier-popover',
  anchorRef: pillRef,
  popoverRef,
  singleOpen: true,
  closeOnEscape: true,
  escapeCapture: true,
  returnFocusOnEscape: true,
  closeOnOutside: ['pointerdown'],
  closeOnScroll: true,
  closeOnResize: true,
  position: { width: 288, margin: 8, gap: 8 },
})
const popoverId = popover.id
const popoverNameId = `${popover.id}-name`
const popoverPosition = popover.position
const isOpen = popover.isOpen

const tierEndDate = computed<string | null>(() => {
  const endsAt = props.pricing.current_tier?.ends_at
  return endsAt === null || endsAt === undefined ? null : formatPriceChangeDate(endsAt, locale.value)
})

const tierEndsFirst = computed<boolean>(
  () => tierEndDate.value !== null && props.pricing.current_tier?.remaining_spots !== null && props.pricing.current_tier?.remaining_spots !== undefined,
)

function togglePopover(): void {
  popover.toggle()
}
</script>

<template>
  <span v-if="tierName" class="contents">
    <button
      ref="pillRef"
      type="button"
      class="inline-flex items-center gap-1 rounded-full bg-sky-50 hover:bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-700 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
      aria-haspopup="dialog"
      :aria-expanded="isOpen"
      :aria-controls="isOpen ? popoverId : undefined"
      data-testid="race-price-tier"
      @click="togglePopover"
    >
      {{ tierName }}
      <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </button>
    <Teleport to="body">
      <div
        v-if="isOpen"
        :id="popoverId"
        ref="popoverRef"
        role="dialog"
        :aria-labelledby="popoverNameId"
        class="fixed z-50 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-lg"
        :style="{ top: popoverPosition.top + 'px', left: popoverPosition.left + 'px', width: popoverPosition.width + 'px' }"
        data-testid="race-price-tier-popover"
      >
        <p :id="popoverNameId" class="text-sm font-semibold text-slate-900" data-testid="race-price-popover-name">{{ tierName }}</p>
        <p class="mt-2 flex items-baseline gap-2">
          <span class="text-lg font-bold text-slate-900" data-testid="race-price-popover-price">{{ priceText }}</span>
          <s v-if="standardPriceText" class="text-sm text-slate-400" data-testid="race-price-popover-standard">
            <span class="sr-only">{{ t('public.race_price.standard_price') }}</span>
            {{ standardPriceText }}
          </s>
        </p>
        <p v-if="tierEndDate" class="mt-2 text-xs text-slate-600" data-testid="race-price-popover-until">
          {{ t('public.race_price.valid_until', { date: tierEndDate }) }}
        </p>
        <p v-if="spotsText" class="mt-1 text-xs text-slate-600" data-testid="race-price-popover-spots">{{ spotsText }}</p>
        <p v-if="tierEndsFirst" class="mt-2 text-xs text-slate-500" data-testid="race-price-popover-first">
          {{ t('public.race_price.ends_first') }}
        </p>
      </div>
    </Teleport>
  </span>
</template>
